/**
 * CallsPanel
 * TMD-style stack of call cards. Each card is one call (active or past); click
 * to expand and see brief context + suggested questions + notes textarea.
 * Active calls pinned at top, auto-expanded, with a red Hangup button. Past
 * calls collapsed by default.
 *
 * Source of truth for the stack is the Photon Dial extension's `recentCalls`
 * ring buffer (chrome.storage.session, last ~20). Notes save to GHL as a
 * single structured note on hangup with a `[Photon Call]` prefix so the
 * record is permanent and findable in the contact's timeline.
 *
 * Architecture quirk: portal can't reach loopback broker directly (HTTPS →
 * HTTP private network blocked), so dial/hangup/state go through the
 * extension's content-script bridge. See photonDialBridge.ts.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Spinner,
  Badge,
  Textarea,
  Input,
  mergeClasses,
} from "@fluentui/react-components";
import {
  Sparkle16Regular,
  Open16Regular,
  CallEnd16Regular,
  Mail16Regular,
  Call16Regular,
  ChevronDown16Regular,
  ChevronRight16Regular,
  Add16Regular,
  Phone16Regular,
} from "@fluentui/react-icons";
import useCrmApiClient from "../../hooks/useCrmApiClient";
import { brokerCommand, onBrokerEvent, BrokerCallEvent, RecentCallEntry } from "./photonDialBridge";

type CrmApiClient = ReturnType<typeof useCrmApiClient>;

interface BriefResponse {
  found: boolean;
  phone?: string;
  contactId?: string;
  name?: string | null;
  company?: string | null;
  email?: string | null;
  url?: string | null;
  title?: string | null;
  tags?: string[];
  dnd?: boolean;
  openOpps?: Array<{
    id?: string;
    name?: string;
    stageName?: string | null;
    pipelineStageId?: string;
    monetaryValue?: number;
    updatedAt?: string;
    url?: string | null;
  }>;
  openOppCount?: number;
  recentNotes?: Array<{ id?: string; body?: string; dateAdded?: string }>;
  noteCount?: number;
  recentConversations?: Array<{
    id?: string;
    type?: string;
    lastMessageDate?: number | string;
    lastMessageBody?: string;
  }>;
  conversationCount?: number;
}

interface Props {
  crmApi: CrmApiClient;
  /** When set (from inbound deep-link), auto-expand the matching card. */
  focusCallId?: string | null;
  onAskWithAI: (prompt: string, title?: string, autoSend?: boolean) => void;
  buildPrompt: (args: {
    from: string;
    contactId: string | null;
    name: string | null;
    company: string | null;
  }) => string;
}

interface CardSaveState {
  saving: boolean;
  savedAt?: number;
  savedText?: string;
  error?: string;
}

const useStyles = makeStyles({
  // Mirror TMD's single-container layout: one flex:1 minHeight:0 overflowY:auto
  // wrapper holds everything. Dial bar sits sticky at the top, cards flow
  // below. This is the layout pattern that actually works for scrollable
  // tabbed content in this app — earlier nested-root attempts kept hitting
  // height-resolution problems where the stack never knew its parent's
  // height and so its overflow rule never engaged.
  container: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
    overflowY: "auto",
  },
  dialBar: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
    background: tokens.colorNeutralBackground2,
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  dialError: {
    fontSize: "12px",
    color: tokens.colorPaletteRedForeground1,
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    fontSize: "13px",
    textAlign: "center",
    padding: tokens.spacingVerticalXXXL,
  },
  card: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    background: tokens.colorNeutralBackground1,
    overflow: "hidden",
    // CRITICAL: column flex children default to flex-shrink:1, so when the
    // stack overflows the column parent, all cards shrink proportionally
    // and content gets clipped instead of triggering parent scroll. Pinning
    // flex-shrink:0 forces each card to its natural content height; the
    // parent container's overflowY:auto then engages.
    flexShrink: 0,
  },
  cardActive: {
    borderTop: `1px solid ${tokens.colorBrandStroke1}`,
    borderRight: `1px solid ${tokens.colorBrandStroke1}`,
    borderBottom: `1px solid ${tokens.colorBrandStroke1}`,
    borderLeft: `4px solid ${tokens.colorBrandStroke1}`,
  },
  cardEnded: {
    background: tokens.colorNeutralBackground2,
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingHorizontalM,
  },
  cardHeaderMain: { flex: 1, minWidth: 0 },
  eyebrow: {
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: tokens.colorNeutralForeground3,
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  headline: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: 1.3,
    marginTop: "2px",
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  metaLine: {
    fontSize: "13px",
    color: tokens.colorNeutralForeground3,
    marginTop: "4px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  metaSavedOk: { color: tokens.colorPaletteGreenForeground1 },
  metaSavedErr: { color: tokens.colorPaletteRedForeground1 },
  expandLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    marginTop: tokens.spacingVerticalS,
    fontSize: "13px",
    color: tokens.colorNeutralForeground2,
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "pointer",
    "&:hover": { color: tokens.colorNeutralForeground1 },
  },
  statePill: {
    fontSize: "10px",
    padding: "2px 6px",
    borderRadius: "999px",
    background: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    flexShrink: 0,
  },
  statePillActive: {
    background: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  statePillRinging: {
    background: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  body: {
    padding: "0 16px 16px",
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  bodyTwoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: tokens.spacingHorizontalL,
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  },
  contactBlock: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground2,
    lineHeight: 1.5,
    paddingTop: tokens.spacingVerticalS,
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
    marginTop: "4px",
  },
  metaItem: { display: "inline-flex", alignItems: "center", gap: "4px" },
  tagRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    marginTop: tokens.spacingVerticalS,
  },
  sectionTitle: {
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: tokens.colorNeutralForeground3,
    marginBottom: "6px",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  summary: {
    fontSize: "13px",
    color: tokens.colorNeutralForeground1,
    marginTop: "4px",
    lineHeight: 1.4,
  },
  oppRow: {
    padding: "4px 0",
    fontSize: "13px",
    color: tokens.colorNeutralForeground2,
    display: "flex",
    justifyContent: "space-between",
    gap: "8px",
  },
  oppName: { fontWeight: 600, color: tokens.colorNeutralForeground1 },
  oppValue: { color: tokens.colorBrandForeground1, fontWeight: 600 },
  oppMeta: { fontSize: "11px", color: tokens.colorNeutralForeground3, marginTop: "2px" },
  noteRowFlow: {
    fontSize: "13px",
    color: tokens.colorNeutralForeground2,
    marginTop: "6px",
    lineHeight: 1.4,
  },
  noteAttribution: { fontSize: "11px", color: tokens.colorNeutralForeground3, marginBottom: "2px" },
  bodyFlow: {
    padding: "12px 16px",
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    display: "flex",
    flexDirection: "column",
  },
  bodyScroll: {
    // No height cap — body grows with content. The main stack handles
    // scroll if multiple expanded cards exceed the viewport. Earlier
    // attempt to constrain the body to 320px made cards feel cramped.
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  questionsBlock: {
    marginTop: tokens.spacingVerticalS,
    fontSize: "13px",
    color: tokens.colorNeutralForeground2,
  },
  questionsListInline: {
    margin: "4px 0 0 0",
    paddingLeft: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  bottomActions: {
    display: "flex",
    gap: tokens.spacingHorizontalL,
    flexWrap: "wrap",
    paddingTop: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  bottomAction: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "13px",
    color: tokens.colorBrandForeground1,
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "pointer",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
    "&:disabled": { color: tokens.colorNeutralForeground3, cursor: "not-allowed", textDecoration: "none" },
  },
  inlinePanel: {
    background: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  inlinePanelLabel: {
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: tokens.colorNeutralForeground3,
    fontWeight: 600,
  },
  inlineActionRow: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    justifyContent: "flex-end",
    alignItems: "center",
  },
  draftField: {
    display: "grid",
    gridTemplateColumns: "60px 1fr",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
    fontSize: "13px",
  },
  draftFieldLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  draftSmallInput: {
    padding: "4px 8px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: "13px",
    fontFamily: "inherit",
  },
  draftBody: {
    width: "100%",
    minHeight: "180px",
    padding: "8px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: "13px",
    fontFamily: "inherit",
    resize: "vertical",
    boxSizing: "border-box",
  },
  hypothesisBlock: {
    background: tokens.colorBrandBackground2,
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
    padding: "10px 12px",
    borderRadius: tokens.borderRadiusSmall,
  },
  hypothesisLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "10px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: tokens.colorBrandForeground1,
    fontWeight: 600,
    marginBottom: "4px",
  },
  hypothesisText: {
    fontSize: "13px",
    color: tokens.colorNeutralForeground1,
    lineHeight: 1.4,
  },
  hypothesisGrounding: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    marginTop: "4px",
    fontStyle: "italic",
  },
  subGroup: {
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    paddingTop: "8px",
    marginTop: "4px",
  },
  subGroupHeader: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: tokens.colorNeutralForeground2,
    fontWeight: 600,
    cursor: "pointer",
    background: "transparent",
    border: "none",
    padding: 0,
    "&:hover": { color: tokens.colorNeutralForeground1 },
  },
  subGroupBody: {
    paddingTop: "6px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  noteRow: {
    padding: "4px 8px",
    fontSize: "11px",
    borderLeft: `2px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: "4px",
    color: tokens.colorNeutralForeground2,
    lineHeight: 1.4,
  },
  noteDate: { fontSize: "10px", color: tokens.colorNeutralForeground3 },
  questionsList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    fontSize: "12px",
    margin: 0,
    paddingLeft: "20px",
  },
  notesArea: { width: "100%", minHeight: "100px" },
  actionRow: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },
  hangup: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    "&:hover": { backgroundColor: tokens.colorPaletteRedBackground3 },
  },
  empty12: { fontSize: "11px", color: tokens.colorNeutralForeground3, fontStyle: "italic" },
  loading: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground2,
    fontSize: "12px",
  },
});

function formatMoney(cents: number | undefined): string {
  if (cents == null) return "";
  const dollars = cents / 100;
  return `$${dollars.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatDateShort(s: string | number | undefined | null): string {
  if (!s) return "";
  const d = typeof s === "number" ? new Date(s) : new Date(s);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatTime(ms: number | null | undefined): string {
  if (!ms) return "";
  return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatDuration(startMs: number | null | undefined, endMs: number | null | undefined): string {
  if (!startMs || !endMs) return "";
  const sec = Math.max(0, Math.round((endMs - startMs) / 1000));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function isActive(call: RecentCallEntry): boolean {
  return call.state !== "ended";
}

function suggestQuestions(brief: BriefResponse | null): string[] {
  const out: string[] = [];
  if (!brief?.found) {
    out.push("Confirm the caller's name and which company they're with.");
    out.push("Ask how you can help today.");
    return out;
  }
  const tags = (brief.tags || []).map((t) => t.toLowerCase());
  if (tags.includes("bwc-pilot")) {
    out.push("How is the body-worn camera pilot deployment going? Any blockers?");
  }
  if (tags.includes("lenslock-employee")) {
    out.push("Internal call — confirm the topic before sharing customer details.");
  }
  const opps = brief.openOpps || [];
  if (opps.length > 0) {
    const top = opps[0];
    out.push(`Where are things on "${top.name}"${top.stageName ? ` (${top.stageName})` : ""}?`);
  }
  if (brief.recentConversations && brief.recentConversations.length > 0) {
    out.push("Did our last message land OK? Any follow-up needed there?");
  }
  if (out.length < 2) out.push("What prompted the call today?");
  return out.slice(0, 4);
}

function buildStructuredNote(call: RecentCallEntry, userNotes: string): string {
  const dirLabel = call.direction === "out" ? "Outbound" : "Inbound";
  const name = call.contact?.name ? `${call.contact.name}${call.contact?.company ? ` (${call.contact.company})` : ""}` : (call.from || "unknown");
  const start = call.startedAt ? new Date(call.startedAt) : null;
  const timeStr = start ? `${start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} ${start.toLocaleDateString([], { month: "numeric", day: "numeric" })}` : "";
  const dur = formatDuration(call.answeredAt ?? call.startedAt, call.endedAt);
  const parts = [`[Photon Call] ${dirLabel} · ${name}`];
  if (timeStr) parts.push(timeStr);
  if (dur) parts.push(dur);
  const header = parts.join(" · ");
  const trimmed = userNotes.trim();
  return trimmed ? `${header}\n\n${trimmed}` : header;
}

interface DraftEmailState {
  status: "drafting" | "ready" | "error";
  toEmail?: string;
  toName?: string;
  subject?: string;
  body?: string;
  reason?: string;
}

interface HypothesisState {
  status: "loading" | "ready" | "unavailable";
  hypothesis?: string;
  grounding?: string;
  reason?: string;
}

interface CardProps {
  call: RecentCallEntry;
  expanded: boolean;
  brief: BriefResponse | null;
  briefLoading: boolean;
  briefError: string | null;
  notes: string;
  saveState: CardSaveState | null;
  hangupBusy: boolean;
  hangupError: string | null;
  noteEditorOpen: boolean;
  draftEmail: DraftEmailState | null;
  hypothesis: HypothesisState | null;
  onToggle: () => void;
  onNotesChange: (text: string) => void;
  onSaveNote: () => void;
  onOpenNoteEditor: () => void;
  onCloseNoteEditor: () => void;
  onHangup: () => void;
  onAskWithAI: () => void;
  onDraftEmail: () => void;
  onCloseDraftEmail: () => void;
  onUpdateDraftField: (field: "subject" | "body" | "toEmail", value: string) => void;
  onOpenInCrm?: () => void;
}

const CallCard: React.FC<CardProps> = ({
  call,
  expanded,
  brief,
  briefLoading,
  briefError,
  notes,
  saveState,
  hangupBusy,
  hangupError,
  noteEditorOpen,
  draftEmail,
  hypothesis,
  onToggle,
  onNotesChange,
  onSaveNote,
  onOpenNoteEditor,
  onCloseNoteEditor,
  onHangup,
  onAskWithAI,
  onDraftEmail,
  onCloseDraftEmail,
  onUpdateDraftField,
  onOpenInCrm,
}) => {
  const styles = useStyles();
  const active = isActive(call);
  const ended = call.state === "ended";
  const direction = call.direction || "in";
  const time = formatTime(call.startedAt);
  const dur = ended ? formatDuration(call.answeredAt ?? call.startedAt, call.endedAt) : "";
  const questions = suggestQuestions(brief);
  const [notesGroupOpen, setNotesGroupOpen] = useState(false);
  const [convsGroupOpen, setConvsGroupOpen] = useState(false);

  const saveLabel = saveState?.saving
    ? "saving…"
    : saveState?.error
      ? `save failed: ${saveState.error}`
      : saveState?.savedAt
        ? `saved ${formatTime(saveState.savedAt)}`
        : null;

  // Eyebrow: DIRECTION · NAME (COMPANY) · NUMBER (plain text — no inline link;
  // Open in CRM at the bottom is the single "go to GHL" action).
  const dirLabel = direction === "out" ? "OUTBOUND" : "INBOUND";
  const displayName = brief?.name ?? call.contact?.name ?? null;
  const displayCompany = brief?.company ?? call.contact?.company ?? null;
  const contactUrl = brief?.url ?? call.contact?.url ?? null;

  // Headline: "Inbound call · Answered · 1m 23s" with state badge alongside.
  const stateLabel =
    call.state === "ringing" ? "Ringing"
      : call.state === "initiated" ? (direction === "out" ? "Dialing" : "Ringing")
        : call.state === "answered" ? "Answered"
          : call.state === "ended" ? "Ended"
            : call.state;
  const headlineSegments = [direction === "out" ? "Outbound call" : "Inbound call", stateLabel];
  if (dur) headlineSegments.push(dur);
  const headlineText = headlineSegments.join(" · ");

  // Meta line: time · save status (company in eyebrow, op count in summary).
  const metaSegments: React.ReactNode[] = [];
  if (time) metaSegments.push(<span key="t">{time}</span>);
  if (saveLabel) {
    metaSegments.push(
      <span
        key="save"
        className={mergeClasses(
          !!saveState?.error && styles.metaSavedErr,
          !!saveState?.savedAt && !saveState?.error && styles.metaSavedOk,
        )}
      >{saveLabel}</span>,
    );
  }

  // Summary line: one-sentence "what this caller is about." Derived from the
  // brief — opp count + top opp + last note. Empty if brief hasn't loaded.
  let summary = "";
  if (brief?.found) {
    const parts: string[] = [];
    const oppCount = brief.openOppCount || 0;
    if (oppCount > 0 && brief.openOpps && brief.openOpps.length > 0) {
      const totalValue = brief.openOpps.reduce((acc, o) => acc + (o.monetaryValue || 0), 0);
      const top = brief.openOpps[0];
      const valStr = totalValue ? ` (${formatMoney(totalValue)} total` : "";
      const topName = top.name ? `top: ${top.name}` : "";
      const stage = top.stageName ? ` at ${top.stageName}` : "";
      const tail = topName ? `${valStr ? `${valStr}, ` : " ("}${topName}${stage})` : (valStr ? `${valStr})` : "");
      parts.push(`${oppCount} open opp${oppCount === 1 ? "" : "s"}${tail}`);
    }
    const newest = brief.recentNotes && brief.recentNotes.length > 0 ? brief.recentNotes[0] : null;
    if (newest?.dateAdded) parts.push(`Last note ${formatDateShort(newest.dateAdded)}`);
    summary = parts.join(". ") + (parts.length > 0 ? "." : "");
  }

  return (
    <div className={mergeClasses(styles.card, active && styles.cardActive, ended && !active && styles.cardEnded)}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderMain}>
          <div className={styles.eyebrow}>
            {dirLabel}
            {displayName && (
              <>
                {" · "}
                {displayName.toUpperCase()}
                {displayCompany && ` (${displayCompany.toUpperCase()})`}
              </>
            )}
            {call.from && ` · ${call.from}`}
          </div>
          <div className={styles.headline}>
            <span>{headlineText}</span>
            <span className={mergeClasses(
              styles.statePill,
              active && styles.statePillActive,
              call.state === "ringing" && styles.statePillRinging,
            )}>
              {active ? "ACTIVE" : "ENDED"}
            </span>
          </div>
          {metaSegments.length > 0 && (
            <div className={styles.metaLine}>
              {metaSegments.map((seg, i) => (
                <React.Fragment key={i}>{i > 0 && " · "}{seg}</React.Fragment>
              ))}
            </div>
          )}
          {summary && <div className={styles.summary}>{summary}</div>}
          <button type="button" className={styles.expandLink} onClick={onToggle}>
            {expanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
            {expanded ? "Hide notes & context" : "Show notes & context"}
          </button>
        </div>
        <div style={{ display: "flex", gap: tokens.spacingHorizontalS, flexShrink: 0 }}>
          {active && (
            <Button
              size="small"
              appearance="primary"
              className={styles.hangup}
              icon={hangupBusy ? <Spinner size="tiny" /> : <CallEnd16Regular />}
              disabled={hangupBusy || !call.callId}
              onClick={onHangup}
            >
              Hang up
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div className={styles.bodyFlow}>
          <div className={styles.bodyScroll}>
          {hangupError && <div style={{ fontSize: "12px", color: tokens.colorPaletteRedForeground1 }}>Hangup failed: {hangupError}</div>}
          {briefLoading && <div className={styles.loading}><Spinner size="tiny" /> Pulling notes, opps, conversations…</div>}
          {briefError && <div className={styles.empty12}>Could not fetch brief: {briefError}</div>}

          {brief?.found && (
            <>
              {/* Likely-calling-about hypothesis: inbound only, while the
                  call is still active (after hangup the user knows what was
                  discussed — no need to second-guess with an AI hypothesis). */}
              {direction === "in" && active && hypothesis && (
                <div className={styles.hypothesisBlock}>
                  <div className={styles.hypothesisLabel}>
                    <Sparkle16Regular /> Likely calling about
                  </div>
                  {hypothesis.status === "loading" && (
                    <div className={styles.loading}><Spinner size="tiny" /> Inferring…</div>
                  )}
                  {hypothesis.status === "ready" && (
                    <>
                      <div className={styles.hypothesisText}>{hypothesis.hypothesis}</div>
                      {hypothesis.grounding && <div className={styles.hypothesisGrounding}>{hypothesis.grounding}</div>}
                    </>
                  )}
                  {hypothesis.status === "unavailable" && (
                    <div className={styles.hypothesisGrounding}>{hypothesis.reason || "Not enough context to guess."}</div>
                  )}
                </div>
              )}

              {brief.email && <div className={styles.metaLine} style={{ marginTop: 0 }}><Mail16Regular style={{ verticalAlign: "middle", marginRight: "4px" }} />{brief.email}{brief.dnd && <span style={{ marginLeft: "8px" }}><Badge color="danger" size="small">DND</Badge></span>}</div>}
              {brief.tags && brief.tags.length > 0 && (
                <div className={styles.tagRow}>
                  {brief.tags.slice(0, 6).map((t) => <Badge key={t} appearance="tint" size="small">{t}</Badge>)}
                </div>
              )}
              {brief.openOpps && brief.openOpps.length > 0 && (
                <div>
                  {brief.openOpps.slice(0, 3).map((opp) => (
                    <div key={opp.id} className={styles.oppRow}>
                      <div>
                        <span className={styles.oppName}>{opp.name || "(unnamed)"}</span>
                        {(opp.stageName || opp.updatedAt) && (
                          <div className={styles.oppMeta}>
                            {opp.stageName || ""}
                            {opp.updatedAt && `${opp.stageName ? " · " : ""}updated ${formatDateShort(opp.updatedAt)}`}
                          </div>
                        )}
                      </div>
                      {opp.monetaryValue != null && <span className={styles.oppValue}>{formatMoney(opp.monetaryValue)}</span>}
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.questionsBlock}>
                <strong>Suggested questions:</strong>
                <ol className={styles.questionsListInline}>
                  {questions.map((q, i) => <li key={i}>{q}</li>)}
                </ol>
              </div>

              {/* Recent notes + conversations behind collapsible groups —
                  hypothesis is the headline; raw items are one click away. */}
              {brief.recentNotes && brief.recentNotes.length > 0 && (
                <div className={styles.subGroup}>
                  <button type="button" className={styles.subGroupHeader} onClick={() => setNotesGroupOpen((v) => !v)}>
                    {notesGroupOpen ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                    Recent notes ({brief.noteCount || brief.recentNotes.length})
                  </button>
                  {notesGroupOpen && (
                    <div className={styles.subGroupBody}>
                      {brief.recentNotes.slice(0, 5).map((n) => (
                        <div key={n.id} className={styles.noteRowFlow}>
                          <div className={styles.noteAttribution}>{formatDateShort(n.dateAdded)}</div>
                          {n.body}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {brief.recentConversations && brief.recentConversations.length > 0 && (
                <div className={styles.subGroup}>
                  <button type="button" className={styles.subGroupHeader} onClick={() => setConvsGroupOpen((v) => !v)}>
                    {convsGroupOpen ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                    Recent conversations ({brief.conversationCount || brief.recentConversations.length})
                  </button>
                  {convsGroupOpen && (
                    <div className={styles.subGroupBody}>
                      {brief.recentConversations.slice(0, 3).map((c) => (
                        <div key={c.id} className={styles.noteRowFlow}>
                          <div className={styles.noteAttribution}>
                            {formatDateShort(c.lastMessageDate)}
                            {c.type && ` · ${c.type.replace(/^TYPE_/, "").toLowerCase()}`}
                          </div>
                          {c.lastMessageBody}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {brief && !brief.found && <div className={styles.empty12}>No GHL contact match for {call.from}.</div>}

          {/* Inline note editor — only shown when user clicks "+ New note" */}
          {noteEditorOpen && (
            <div className={styles.inlinePanel}>
              <div className={styles.inlinePanelLabel}>New note</div>
              <Textarea
                value={notes}
                onChange={(_e, data) => onNotesChange(data.value)}
                placeholder="Note saves as a structured [Photon Call] entry on the contact when you click Save."
                resize="vertical"
                style={{ minHeight: "100px", width: "100%" }}
              />
              <div className={styles.inlineActionRow}>
                {saveState?.error && <span style={{ fontSize: "12px", color: tokens.colorPaletteRedForeground1, marginRight: "auto" }}>{saveState.error}</span>}
                {saveState?.savedAt && !saveState.error && <span style={{ fontSize: "12px", color: tokens.colorPaletteGreenForeground1, marginRight: "auto" }}>Saved {formatTime(saveState.savedAt)}</span>}
                <Button size="small" appearance="subtle" onClick={onCloseNoteEditor}>Cancel</Button>
                <Button
                  size="small"
                  appearance="primary"
                  disabled={saveState?.saving || !notes.trim()}
                  onClick={onSaveNote}
                >
                  {saveState?.saving ? <Spinner size="tiny" /> : "Save"}
                </Button>
              </div>
            </div>
          )}

          {/* Inline draft email panel — appears when "Draft email" is clicked */}
          {draftEmail && (
            <div className={styles.inlinePanel}>
              <div className={styles.inlinePanelLabel}>Follow-up email — drafted by AI, edit before sending</div>
              {draftEmail.status === "drafting" && (
                <div className={styles.loading}><Spinner size="tiny" /> Drafting…</div>
              )}
              {draftEmail.status === "error" && (
                <div className={styles.empty12} style={{ color: tokens.colorPaletteRedForeground1 }}>{draftEmail.reason || "Draft failed."}</div>
              )}
              {draftEmail.status === "ready" && (
                <>
                  <div className={styles.draftField}>
                    <div className={styles.draftFieldLabel}>To</div>
                    <input
                      type="email"
                      className={styles.draftSmallInput}
                      value={draftEmail.toEmail || ""}
                      onChange={(e) => onUpdateDraftField("toEmail", e.target.value)}
                    />
                  </div>
                  <div className={styles.draftField}>
                    <div className={styles.draftFieldLabel}>Subject</div>
                    <input
                      type="text"
                      className={styles.draftSmallInput}
                      value={draftEmail.subject || ""}
                      onChange={(e) => onUpdateDraftField("subject", e.target.value)}
                    />
                  </div>
                  <textarea
                    className={styles.draftBody}
                    value={draftEmail.body || ""}
                    onChange={(e) => onUpdateDraftField("body", e.target.value)}
                  />
                </>
              )}
              <div className={styles.inlineActionRow}>
                <Button size="small" appearance="subtle" onClick={onCloseDraftEmail}>Close</Button>
                {draftEmail.status === "ready" && (
                  <Button
                    size="small"
                    appearance="primary"
                    icon={<Mail16Regular />}
                    onClick={() => {
                      const to = encodeURIComponent(draftEmail.toEmail || "");
                      const subject = encodeURIComponent(draftEmail.subject || "");
                      const body = encodeURIComponent(draftEmail.body || "");
                      window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
                    }}
                  >
                    Open in mail
                  </Button>
                )}
              </div>
            </div>
          )}

          </div>
          {/* Bottom action row — TMD-style text-link buttons. Outside the
              scroll container so it stays visible at the bottom of the card. */}
          <div className={styles.bottomActions}>
            <button
              type="button"
              className={styles.bottomAction}
              onClick={onOpenNoteEditor}
              disabled={noteEditorOpen}
            >
              <Add16Regular /> New note
            </button>
            <button
              type="button"
              className={styles.bottomAction}
              onClick={onDraftEmail}
              disabled={!brief?.email || (draftEmail !== null && draftEmail.status !== "error")}
              title={!brief?.email ? "No email on contact" : undefined}
            >
              <Mail16Regular /> Draft email
            </button>
            <button
              type="button"
              className={styles.bottomAction}
              onClick={onAskWithAI}
              disabled={!brief?.contactId && !call.from}
            >
              <Sparkle16Regular /> Brief me with AI
            </button>
            {(contactUrl || onOpenInCrm) && (
              <button
                type="button"
                className={styles.bottomAction}
                onClick={() => {
                  if (onOpenInCrm) onOpenInCrm();
                  else if (contactUrl) window.open(contactUrl, "_blank", "noopener");
                }}
              >
                <Open16Regular /> Open in CRM
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const CallsPanel: React.FC<Props> = ({ crmApi, focusCallId, onAskWithAI, buildPrompt }) => {
  const styles = useStyles();

  const [recentCalls, setRecentCalls] = useState<RecentCallEntry[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [notesByCallId, setNotesByCallId] = useState<Record<string, string>>({});
  const [saveStateByCallId, setSaveStateByCallId] = useState<Record<string, CardSaveState>>({});
  const [briefByCallId, setBriefByCallId] = useState<Record<string, BriefResponse>>({});
  const [briefLoading, setBriefLoading] = useState<Set<string>>(new Set());
  const [briefError, setBriefError] = useState<Record<string, string>>({});
  const [hangupBusy, setHangupBusy] = useState<Set<string>>(new Set());
  const [hangupError, setHangupError] = useState<Record<string, string>>({});
  const [autoExpandedActive, setAutoExpandedActive] = useState<Set<string>>(new Set());
  const [noteEditorOpen, setNoteEditorOpen] = useState<Set<string>>(new Set());
  const [draftEmailByCallId, setDraftEmailByCallId] = useState<Record<string, DraftEmailState>>({});
  const [hypothesisByCallId, setHypothesisByCallId] = useState<Record<string, HypothesisState>>({});

  const [dialInput, setDialInput] = useState("");
  const [dialBusy, setDialBusy] = useState(false);
  const [dialError, setDialError] = useState<string | null>(null);

  // Load initial state + subscribe to live updates.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await brokerCommand("get-state");
        if (cancelled) return;
        const initial = (resp.recentCalls as RecentCallEntry[]) || [];
        setRecentCalls(initial);
      } catch (_) { /* extension not present */ }
    })();
    const unsub = onBrokerEvent((msg) => {
      if (cancelled) return;
      // TEMP diagnostic: log every broker event the portal sees so we can
      // tell if hangup broadcasts are arriving live.
      // eslint-disable-next-line no-console
      console.log("[CallsPanel] broker event:", msg.type, msg.payload);
      if (msg.type === "recent-calls") setRecentCalls(msg.payload);
      if (msg.type === "event" && msg.payload?.event === "call") {
        const data = msg.payload.data as BrokerCallEvent & {
          available?: boolean;
          hypothesis?: string;
          grounding?: string;
          reason?: string;
        };
        if (data?.type === "hypothesis-ready" && data.callId) {
          const cid = data.callId;
          setHypothesisByCallId((prev) => ({
            ...prev,
            [cid]: data.available && data.hypothesis
              ? { status: "ready", hypothesis: data.hypothesis, grounding: data.grounding }
              : { status: "unavailable", reason: data.reason || "Not enough context to guess." },
          }));
        }
      }
    });
    return () => { cancelled = true; unsub(); };
  }, []);

  // Auto-expand any active call once. Also auto-expand the focus callId
  // (from inbound deep-link) when it shows up in the list.
  useEffect(() => {
    setExpanded((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const c of recentCalls) {
        if (isActive(c) && !autoExpandedActive.has(c.callId)) {
          next.add(c.callId);
          changed = true;
        }
        if (focusCallId && c.callId === focusCallId && !next.has(c.callId)) {
          next.add(c.callId);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setAutoExpandedActive((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const c of recentCalls) {
        if (isActive(c) && !next.has(c.callId)) {
          next.add(c.callId);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [recentCalls, focusCallId, autoExpandedActive]);

  // Lazy brief fetch on first expansion (per call).
  const fetchBrief = useCallback(async (callId: string, phone: string) => {
    if (briefByCallId[callId]) return;
    if (briefLoading.has(callId)) return;
    setBriefLoading((prev) => new Set(prev).add(callId));
    setBriefError((prev) => { const n = { ...prev }; delete n[callId]; return n; });
    try {
      const data = await crmApi.get<BriefResponse>(`/api/contacts/by-phone-brief?phone=${encodeURIComponent(phone)}`);
      setBriefByCallId((prev) => ({ ...prev, [callId]: data }));
    } catch (e: unknown) {
      setBriefError((prev) => ({ ...prev, [callId]: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBriefLoading((prev) => { const n = new Set(prev); n.delete(callId); return n; });
    }
  }, [briefByCallId, briefLoading, crmApi]);

  // When a card becomes/stays expanded, ensure brief is fetched.
  useEffect(() => {
    for (const callId of expanded) {
      const call = recentCalls.find((c) => c.callId === callId);
      if (!call?.from) continue;
      if (!briefByCallId[callId] && !briefLoading.has(callId)) {
        fetchBrief(callId, call.from);
      }
    }
  }, [expanded, recentCalls, briefByCallId, briefLoading, fetchBrief]);

  // After brief loads for an inbound expanded card, lazy-fetch the AI
  // hypothesis on why the contact is likely calling. Skipped for outbound
  // (rep already knows) or when no GHL contact matched.
  const fetchHypothesis = useCallback(async (callId: string, contactId: string) => {
    if (hypothesisByCallId[callId]) return;
    setHypothesisByCallId((prev) => ({ ...prev, [callId]: { status: "loading" } }));
    try {
      const resp = await crmApi.get<{
        available?: boolean;
        hypothesis?: string;
        grounding?: string;
        reason?: string;
      }>(`/api/contacts/${contactId}/likely-call-reason`);
      if (resp.available && resp.hypothesis) {
        setHypothesisByCallId((prev) => ({
          ...prev,
          [callId]: { status: "ready", hypothesis: resp.hypothesis, grounding: resp.grounding },
        }));
      } else {
        setHypothesisByCallId((prev) => ({
          ...prev,
          [callId]: { status: "unavailable", reason: resp.reason || "Not enough context to guess." },
        }));
      }
    } catch (e: unknown) {
      setHypothesisByCallId((prev) => ({
        ...prev,
        [callId]: { status: "unavailable", reason: e instanceof Error ? e.message : String(e) },
      }));
    }
  }, [crmApi, hypothesisByCallId]);

  useEffect(() => {
    for (const callId of expanded) {
      const call = recentCalls.find((c) => c.callId === callId);
      if (!call) continue;
      if (call.direction !== "in") continue;
      // Skip ended calls — the hypothesis is only useful before/during.
      if (!isActive(call)) continue;
      const brief = briefByCallId[callId];
      if (!brief?.found || !brief.contactId) continue;
      if (hypothesisByCallId[callId]) continue;
      fetchHypothesis(callId, brief.contactId);
    }
  }, [expanded, recentCalls, briefByCallId, hypothesisByCallId, fetchHypothesis]);

  const toggleExpand = useCallback((callId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(callId)) next.delete(callId);
      else next.add(callId);
      return next;
    });
  }, []);

  const setNotes = useCallback((callId: string, text: string) => {
    setNotesByCallId((prev) => ({ ...prev, [callId]: text }));
  }, []);

  // "+ New note" → save → keep card expanded so user sees the saved indicator.
  const saveNote = useCallback(async (call: RecentCallEntry) => {
    const userNotes = notesByCallId[call.callId] || "";
    if (!userNotes.trim()) return;
    const contactId = briefByCallId[call.callId]?.contactId ?? call.contact?.contactId;
    if (!contactId) {
      setSaveStateByCallId((prev) => ({ ...prev, [call.callId]: { saving: false, error: "No GHL contact — note can't be saved" } }));
      return;
    }
    const body = buildStructuredNote(call, userNotes);
    const lastSaved = saveStateByCallId[call.callId];
    if (lastSaved?.savedText === body) return;
    setSaveStateByCallId((prev) => ({ ...prev, [call.callId]: { saving: true } }));
    try {
      await crmApi.post(`/api/contacts/${contactId}/notes`, { body });
      setSaveStateByCallId((prev) => ({ ...prev, [call.callId]: { saving: false, savedAt: Date.now(), savedText: body } }));
      // Close the editor on successful save — the saved indicator stays in the
      // header's meta line ("saved 9:53 AM").
      setNoteEditorOpen((prev) => { const n = new Set(prev); n.delete(call.callId); return n; });
      setNotesByCallId((prev) => ({ ...prev, [call.callId]: "" }));
    } catch (e: unknown) {
      setSaveStateByCallId((prev) => ({ ...prev, [call.callId]: { saving: false, error: e instanceof Error ? e.message : String(e) } }));
    }
  }, [notesByCallId, briefByCallId, saveStateByCallId, crmApi]);

  const draftEmail = useCallback(async (call: RecentCallEntry) => {
    const contactId = briefByCallId[call.callId]?.contactId ?? call.contact?.contactId;
    if (!contactId) {
      setDraftEmailByCallId((prev) => ({ ...prev, [call.callId]: { status: "error", reason: "No GHL contact." } }));
      return;
    }
    setDraftEmailByCallId((prev) => ({ ...prev, [call.callId]: { status: "drafting" } }));
    try {
      const resp = await crmApi.post<{
        draft_available?: boolean;
        reason?: string;
        to_email?: string;
        to_name?: string;
        subject?: string;
        body_plain?: string;
      }>(`/api/contacts/${contactId}/draft-call-followup`, {
        callId: call.callId,
        direction: call.direction,
        startedAt: call.startedAt,
        answeredAt: call.answeredAt,
        endedAt: call.endedAt,
        userNotes: notesByCallId[call.callId] || "",
      });
      if (!resp.draft_available) {
        setDraftEmailByCallId((prev) => ({ ...prev, [call.callId]: { status: "error", reason: resp.reason || "Draft unavailable." } }));
        return;
      }
      setDraftEmailByCallId((prev) => ({
        ...prev,
        [call.callId]: {
          status: "ready",
          toEmail: resp.to_email || "",
          toName: resp.to_name || "",
          subject: resp.subject || "",
          body: resp.body_plain || "",
        },
      }));
    } catch (e: unknown) {
      setDraftEmailByCallId((prev) => ({ ...prev, [call.callId]: { status: "error", reason: e instanceof Error ? e.message : String(e) } }));
    }
  }, [briefByCallId, notesByCallId, crmApi]);

  const updateDraftField = useCallback((callId: string, field: "subject" | "body" | "toEmail", value: string) => {
    setDraftEmailByCallId((prev) => {
      const cur = prev[callId];
      if (!cur || cur.status !== "ready") return prev;
      return { ...prev, [callId]: { ...cur, [field]: value } };
    });
  }, []);

  const hangupCall = useCallback(async (call: RecentCallEntry) => {
    if (!call.callId) return;
    setHangupBusy((prev) => new Set(prev).add(call.callId));
    setHangupError((prev) => { const n = { ...prev }; delete n[call.callId]; return n; });
    try {
      await brokerCommand("hangup", { callId: call.callId });
    } catch (e: unknown) {
      setHangupError((prev) => ({ ...prev, [call.callId]: e instanceof Error ? e.message : String(e) }));
    } finally {
      setHangupBusy((prev) => { const n = new Set(prev); n.delete(call.callId); return n; });
    }
  }, []);

  const handleDial = useCallback(async () => {
    const trimmed = dialInput.trim();
    if (!trimmed) return;
    setDialBusy(true);
    setDialError(null);
    try {
      await brokerCommand("dial", { number: trimmed });
      setDialInput("");
    } catch (e: unknown) {
      setDialError(e instanceof Error ? e.message : String(e));
    } finally {
      setDialBusy(false);
    }
  }, [dialInput]);

  // Active calls pinned top, then ended ones by startedAt desc (recentCalls
  // already comes newest-first from the SW).
  const sorted = [...recentCalls].sort((a, b) => {
    const aActive = isActive(a) ? 1 : 0;
    const bActive = isActive(b) ? 1 : 0;
    if (aActive !== bActive) return bActive - aActive;
    return (b.startedAt || 0) - (a.startedAt || 0);
  });

  return (
    <div className={styles.container}>
      <div className={styles.dialBar}>
        <Input
          type="tel"
          value={dialInput}
          onChange={(_e, data) => setDialInput(data.value)}
          placeholder="Number to dial"
          disabled={dialBusy}
          onKeyDown={(e) => { if (e.key === "Enter") void handleDial(); }}
          style={{ flex: 1 }}
        />
        <Button
          appearance="primary"
          icon={dialBusy ? <Spinner size="tiny" /> : <Call16Regular />}
          disabled={dialBusy || !dialInput.trim()}
          onClick={() => void handleDial()}
        >
          Dial
        </Button>
      </div>
      {dialError && <div className={styles.dialError}>{dialError}</div>}

      {sorted.length === 0 ? (
        <div className={styles.empty}>
          <Phone16Regular style={{ fontSize: "32px", display: "block", margin: "0 auto 8px" }} />
          No calls yet today. Place an outbound call above, or wait for an inbound.
        </div>
      ) : (
          sorted.map((call) => (
            <CallCard
              key={call.callId}
              call={call}
              expanded={expanded.has(call.callId)}
              brief={briefByCallId[call.callId] || null}
              briefLoading={briefLoading.has(call.callId)}
              briefError={briefError[call.callId] || null}
              notes={notesByCallId[call.callId] || ""}
              saveState={saveStateByCallId[call.callId] || null}
              hangupBusy={hangupBusy.has(call.callId)}
              hangupError={hangupError[call.callId] || null}
              noteEditorOpen={noteEditorOpen.has(call.callId)}
              draftEmail={draftEmailByCallId[call.callId] || null}
              hypothesis={hypothesisByCallId[call.callId] || null}
              onToggle={() => toggleExpand(call.callId)}
              onNotesChange={(text) => setNotes(call.callId, text)}
              onSaveNote={() => void saveNote(call)}
              onOpenNoteEditor={() => setNoteEditorOpen((p) => new Set(p).add(call.callId))}
              onCloseNoteEditor={() => {
                setNoteEditorOpen((p) => { const n = new Set(p); n.delete(call.callId); return n; });
                setNotesByCallId((prev) => ({ ...prev, [call.callId]: "" }));
                setSaveStateByCallId((prev) => { const n = { ...prev }; delete n[call.callId]; return n; });
              }}
              onHangup={() => void hangupCall(call)}
              onAskWithAI={() => {
                const brief = briefByCallId[call.callId];
                const prompt = buildPrompt({
                  from: call.from || "",
                  contactId: brief?.contactId ?? call.contact?.contactId ?? null,
                  name: brief?.name ?? call.contact?.name ?? null,
                  company: brief?.company ?? call.contact?.company ?? null,
                });
                const title = (brief?.name || call.contact?.name) ? `${brief?.name || call.contact?.name}${brief?.company || call.contact?.company ? ` (${brief?.company || call.contact?.company})` : ""}` : `Call from ${call.from || ""}`;
                onAskWithAI(prompt, title, true);
              }}
              onDraftEmail={() => void draftEmail(call)}
              onCloseDraftEmail={() => setDraftEmailByCallId((prev) => { const n = { ...prev }; delete n[call.callId]; return n; })}
              onUpdateDraftField={(field, value) => updateDraftField(call.callId, field, value)}
              onOpenInCrm={briefByCallId[call.callId]?.url ? () => window.open(briefByCallId[call.callId].url || "", "_blank", "noopener") : undefined}
            />
          ))
        )}
    </div>
  );
};
